import {CaosCommand, CaosParameter, int, Nullable} from "../caos-def";
import {
    SetVariantFunc,
    VARIANT_FLAG__C1,
    VARIANT_FLAG__C2,
    VARIANT_FLAG__C3,
    VARIANT_FLAG__CV,
    VARIANT_FLAG__DS,
    VARIANT_FLAG__SM
} from "../utils.variants";
import {CaosLib} from "../utils.caosdef";
import {camelToSnakeCase} from "../utils.string";
import VariantsList from "../widgets/variants_list";
import RLValueTag from "../widgets/rlvalue_tag";
import {notNull} from "../utils.arrays";
import React from "react";
import {descriptionWithLinks} from "../utils.commands";


interface CommandProps {
    readonly command: CaosCommand;
    readonly setVariant: SetVariantFunc;
    readonly caosLib: CaosLib;
    readonly idKey: string;
    readonly useSeaMonkeys: boolean;
    readonly pushPopupItem: (kind: string, id: number) => void;
    readonly show: Nullable<boolean>;
}

export default function Command(
    {
        command,
        setVariant,
        caosLib,
        idKey,
        useSeaMonkeys,
        pushPopupItem,
        show
    }: CommandProps
) {

    const returnTypeId = command.return_type_id;
    const returnTypeString = caosLib.getVariableType(returnTypeId)!
    const friendlyReturnTypeString = caosLib.getVariableType(returnTypeId, true);
    // noinspection SpellCheckingInspection
    let rlstring: string = "";
    if (returnTypeString != "command") {
        if (command.rvalue && command.lvalue) {
            rlstring = "r l value";
        } else if (command.rvalue) {
            rlstring = "r-value";
        } else if (command.lvalue) {
            rlstring = "lvalue"
        }
    }

    const rlTags = [];
    if (command.rvalue) {
        rlTags.push("rvalue");
    }
    if (command.lvalue) {
        rlTags.push("lvalue");
    }
    const rlTagElements = rlTags.length > 0
        ? <ol className="rl-tags">
            {rlTags.map(tag => <li key={idKey + "-" + tag}>@{tag}</li>)}
        </ol>
        : "";

    async function copy() {
        const returnType = (returnTypeString[0] == "[") ? returnTypeString : ("(" + returnTypeString + ")");
        const parameters = (command.parameters ?? [])
            .map(parameter => {
                const parameterName = formatParameterName(parameter);
                const parameterType = formatParameterTypeAsString(caosLib, parameter);
                return parameterName + " " + parameterType;
            })
            .join(" ");
        const description = (command.description && command.description.length > 0)
            ? ("\n" + command.description.trim())
                .replace(/\[([^\]]+)]/gi, "$1")
            : "";
        const text = command.command + " " + returnType + " " + parameters + description;
        await navigator.clipboard.writeText(text);
    }

    const isCommand = !(command.rvalue || command.lvalue);

    show = show !== false

    // const variants = bitmaskToVariantArray(command.variants).join(", ");

    const flagsContainer = !isCommand
        ? <RLValueTag command={command}/>
        : "";
    // noinspection HtmlUnknownTarget
    return <article
        data-id={command.id}
        id={idKey}
        data-name-first-letter={command.command[0]}
        data-name={command.command}
        data-entry-type="command"
        data-role="entry"
        data-show={show}
        aria-hidden={!show}
        data-is-command={isCommand}
        className={"command-entry " + (command.parameters && command.parameters.length > 5 ? "long-parameter-list" : "")}
        data-variant-c1={command.variants & VARIANT_FLAG__C1 ? "true" : "false"}
        data-variant-c2={command.variants & VARIANT_FLAG__C2 ? "true" : "false"}
        data-variant-cv={command.variants & VARIANT_FLAG__CV ? "true" : "false"}
        data-variant-c3={command.variants & VARIANT_FLAG__C3 ? "true" : "false"}
        data-variant-ds={command.variants & VARIANT_FLAG__DS ? "true" : "false"}
        data-variant-sm={command.variants & VARIANT_FLAG__SM ? "true" : "false"}
        data-category={command.command_group_id ? caosLib.getCommandGroup(command.command_group_id) : ""}
    >
        <header>
            <h3 aria-label={command.command + ' ' + friendlyReturnTypeString + ' ' + rlstring}>
                <span className="command-name">
                    {command.command}
                </span>
                <span className="command-return-type">({returnTypeString})</span>
            </h3>
            {createParametersElement(caosLib, command, idKey)}
            {rlTagElements}
            <div className="flags-container">
                <VariantsList variants={command.variants} setVariant={setVariant} useSeaMonkeys={useSeaMonkeys}/>
                {flagsContainer}
            </div>
        </header>

        <div className="command-description">
            {descriptionWithLinks(caosLib, idKey, command.variants, command.description, pushPopupItem)}
        </div>
        <div className="copy-container">
            <button onClick={copy}
                    className="copy-button"
                    aria-label={"copy " + command.command + " " + friendlyReturnTypeString}
            >
                <img src="./icons/copy-white.png" alt="copy"/>
            </button>
        </div>
    </article>
}

function createParametersElement(caosLib: CaosLib, command: CaosCommand, commandKey: string): React.JSX.Element | string {
    const parameters = command.parameters
    if (parameters == null || parameters.length === 0) {
        return "";
    }


    return <ol
        className="parameters"
        data-for-command={command.id}
        aria-label={command.command + " parameters"}
    >
        {parameters.map((p, i) => createParameterElement(caosLib, commandKey, p, i))}
    </ol>;
}

function createParameterElement(caosLib: CaosLib, commandKey: string, parameter: CaosParameter, index: int): React.JSX.Element {
    if (parameter == null) {
        console.error("parameter is null and shouldn't be for " + commandKey);
        return <li key={commandKey + '-' + index + '-' + "undef"}>(UNDEFINED)</li>
    }
    const parameterName = formatParameterName(parameter);
    const parameterType = formatParameterType(caosLib, parameter);
    const parameterTypeSimple = caosLib.getVariableType(parameter.type_id)
    return <
        li className="parameter"
           key={commandKey + "-" + index}
           aria-label={parameterName + ' ' + parameterTypeSimple}
    >
        <span className="parameter-name">{parameterName}</span>
        <span className="parameter-type" data-type-id={parameter.type_id}>{parameterType}</span>
    </li>;
}


function formatParameterName(parameter: CaosParameter): string {
    return camelToSnakeCase(parameter.name);
}

function formatParameterType(caosLib: CaosLib, parameter: CaosParameter): React.JSX.Element | string {

    const type = caosLib.getVariableType(parameter.type_id);

    if (type == null || type.trim().length === 0) {
        return "";
    }

    if (type[0] === '[') {
        return type;
    }

    const valuesLists: React.JSX.Element[] = Object.values(parameter.values_lists_ids ?? ([] as int[]))
        .map(listId => {
            const list = caosLib.getValuesList(listId);
            if (!list) {
                return null;
            }
            return <>@<a href={`#value-list-${list.id}`}>{list.name}</a></>
        })
        .filter(notNull);

    const valuesListElement = valuesLists.length > 0 ? valuesLists : "";
    return <>
        ({type}{valuesListElement})
    </>;
}

function formatParameterTypeAsString(caosLib: CaosLib, parameter: CaosParameter): string {
    const type = caosLib.getVariableType(parameter.type_id);

    if (type == null || type.trim().length === 0) {
        return "";
    }

    if (type[0] === '[') {
        return type;
    }
    return "(" + type + ")";
}